import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail } from "lucide-react";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import Bubbles from "../components/Bubbles";
import Breadcrumbs from "../components/Breadcrumbs";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { KNOWN_LIMITATIONS, LAST_REVIEWED } from "../data/accessibility";

export default function Accessibility() {
  useDocumentTitle("Accessibility Statement");
  return (
    <PageTransition>
      <section className="relative overflow-hidden bg-gradient-to-b from-ocean-950 to-ocean-900 pt-32 pb-24 text-center text-foam-50 sm:pt-40 sm:pb-28">
        <Bubbles count={10} />
        <div className="relative mx-auto max-w-2xl px-6">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs font-semibold uppercase tracking-widest text-teal-300"
          >
            Accessibility
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mt-3 font-display text-4xl font-bold sm:text-5xl"
          >
            Accessibility Statement
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-foam-100/70"
          >
            Our commitment to making ZenFresh usable by everyone, and how to tell us when we fall short.
          </motion.p>
        </div>
      </section>

      <Breadcrumbs />

      <section className="mx-auto max-w-2xl px-6 py-24">
        <ScrollReveal>
          <h2 className="font-display text-xl font-bold text-ocean-950">Our Target</h2>
          <p className="mt-3 leading-relaxed text-ocean-800/70">
            ZenFresh is committed to meeting the <strong>Web Content Accessibility Guidelines (WCAG) 2.1
            level AA</strong> across this site. We build with semantic HTML, keyboard navigation, visible focus
            states, and <code>prefers-reduced-motion</code> support baked into every interactive component,
            and we check color contrast against the same standard for every new feature we ship.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.08} className="mt-10">
          <h2 className="font-display text-xl font-bold text-ocean-950">What&apos;s Been Tested</h2>
          <p className="mt-3 leading-relaxed text-ocean-800/70">
            Every feature on this site goes through an internal accessibility review during development —
            keyboard-only navigation, screen-reader landmark structure, focus management in dialogs and
            modals, and contrast checks against our design tokens.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.16} className="mt-10">
          <h2 className="font-display text-xl font-bold text-ocean-950">Known Limitations</h2>
          <ul className="mt-3 space-y-2 text-sm leading-relaxed text-ocean-800/70">
            {KNOWN_LIMITATIONS.map((limitation) => (
              <li key={limitation} className="flex gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-400" />
                {limitation}
              </li>
            ))}
          </ul>
        </ScrollReveal>

        <ScrollReveal delay={0.24} className="mt-10 rounded-2xl bg-foam-100 p-6">
          <h2 className="font-display text-lg font-bold text-ocean-950">Report a Barrier</h2>
          <p className="mt-2 text-sm leading-relaxed text-ocean-800/70">
            If you encounter an accessibility barrier anywhere on this site, please let us know so we can
            fix it.
          </p>
          <Link
            to="/contact"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-teal-600 hover:text-teal-700"
          >
            <Mail className="h-4 w-4" />
            Contact Us
          </Link>
        </ScrollReveal>

        <ScrollReveal delay={0.32} className="mt-10 text-xs uppercase tracking-wide text-ocean-800/40">
          Last reviewed: {LAST_REVIEWED}
        </ScrollReveal>
      </section>
    </PageTransition>
  );
}
